import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Home Page Components
import Hero from './components/Hero';
import StatsSection from './components/StatsSection';
import PortfolioSection from './components/PortfolioSection';
import WealthSolutionsSection from './components/WealthSolutionsSection';
import Web3Section from './components/Web3Section';
import FundingSection from './components/FundingSection';
import LoyaltySection from './components/LoyaltySection';
import ClientSection from './components/ClientSection';
import WhyChooseSection from './components/WhyChooseSection';
import TeamTestimonialsSection from './components/TeamTestimonialsSection';
import FAQSection from './components/FAQSection';
import NextGenSection from './components/NextGenSection';
import LiveChatWidget from './components/LiveChatWidget';

// Web3 Page Components
import Web3DarkHero from './components/web3/Web3DarkHero';
import Web3Hero from './components/web3/Web3Hero';
import Web3Features from './components/web3/Web3Features';
import Web3Audit from './components/web3/Web3Audit';
import Web3ConnectPage from './pages/Web3ConnectPage';
import Web3Demo from './components/web3/Web3Demo';
import WalletTest from './components/web3/WalletTest';
import SimpleWalletTest from './components/web3/SimpleWalletTest';

// Pages
import AboutUsPage from './pages/AboutUsPage';
import ContactUsPage from './pages/ContactUsPage';
import PressReleasePage from './pages/PressReleasePage';
import FaqsPage from './pages/FaqsPage';
import NotFoundPage from './pages/NotFoundPage';
import ClientLoginPage from './pages/ClientLoginPage';
import ApplyNowPage from './pages/ApplyNowPage';
import ServicesPage from './pages/ServicesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Industry Pages
import IndustriesPage from './pages/IndustriesPage';
import BuildingConstructionPage from './pages/industries/BuildingConstructionPage';
import FinancialServicesPage from './pages/industries/FinancialServicesPage';
import CorporateAdvisoryPage from './pages/industries/CorporateAdvisoryPage';
import InternationalMarketsPage from './pages/industries/InternationalMarketsPage';

// Fund Pages
import BlockchainFundPage from './pages/fund/BlockchainFundPage';
import EnergiesFundPage from './pages/fund/EnergiesFundPage';
import SharesTradingPage from './pages/fund/SharesTradingPage';
import AgriculturalFundPage from './pages/fund/AgriculturalFundPage';
import NFPPage from './pages/fund/NFPPage';
import RenewableEnergyPage from './pages/fund/RenewableEnergyPage';

// Service Pages
import InvestmentManagementPage from './pages/services/InvestmentManagementPage';
import FinancialServicePage from './pages/services/FinancialServicePage';
import PortfolioManagementServicePage from './pages/services/PortfolioManagementServicePage';
import FinancialAdvisingPage from './pages/services/FinancialAdvisingPage';
import WealthManagementPage from './pages/services/WealthManagementPage';
import FinancialPlanningPage from './pages/services/FinancialPlanningPage';
import RetirementPlanningPage from './pages/services/RetirementPlanningPage';
import ManagedPortfoliosPage from './pages/services/ManagedPortfoliosPage';
import WealthEstatePlanningPage from './pages/services/WealthEstatePlanningPage';
import MutualFundsPage from './pages/services/MutualFundsPage';
import PersonalRetirementAccountPage from './pages/services/PersonalRetirementAccountPage';
import SecuritiesTradingBrokeragePage from './pages/services/SecuritiesTradingBrokeragePage';
import TrusteeRegistrarServicesPage from './pages/services/TrusteeRegistrarServicesPage';

// Portfolio Pages
import LiquidTokenPage from './pages/portfolio/LiquidTokenPage';
import BitcoinTradingPage from './pages/portfolio/BitcoinTradingPage';
import EarlyStageTokenPage from './pages/portfolio/EarlyStageTokenPage';
import StakingFundPage from './pages/portfolio/StakingFundPage';
import BuyCryptoPage from './pages/BuyCryptoPage';
import EarnCryptoPage from './pages/EarnCryptoPage';
import ExchangePage from './pages/ExchangePage';
import FuturesTradingPage from './pages/FuturesTradingPage';
import TaxReportingPage from './pages/TaxReportingPage';

function App() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const handleLoad = () => {
      setIsLoaded(true);
    };

    if (document.readyState === 'complete') {
      setIsLoaded(true);
    } else {
      window.addEventListener('load', handleLoad);
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, 200);

      return () => {
        window.removeEventListener('load', handleLoad);
        clearTimeout(timer);
      };
    }
  }, []);

  const HomePage = () => (
    <main>
      <Hero />
      <StatsSection />
      <PortfolioSection />
      <WealthSolutionsSection />
      <Web3Section />
      <FundingSection />
      <LoyaltySection />
      <ClientSection />
      <WhyChooseSection />
      <TeamTestimonialsSection />
      <FAQSection />
      <NextGenSection />
    </main>
  );

  const Web3Page = () => (
    <main>
      <Web3DarkHero />
      <Web3Features />
      <Web3Demo />
      <Web3Hero />
      <Web3Audit />
      <FAQSection />
      <NextGenSection />
    </main>
  );

  return (
    <ThemeProvider>
      <div className={`App ${isLoaded ? 'loaded' : 'loading-stabilizer'}`}>
        <Router>
          <ScrollToTop />
          <Header />
          <Routes>
            {/* Home Page */}
            <Route path="/" element={<HomePage />} />
            
            {/* Web3 Page */}
            <Route path="/web3" element={<Web3Page />} />
            
            {/* Web3 Connect Page */}
            <Route path="/web3-connect" element={<Web3ConnectPage />} />
            
            {/* Wallet Test Page */}
            <Route path="/wallet-test" element={<WalletTest />} />
            
            {/* Simple Wallet Test Page */}
            <Route path="/simple-wallet-test" element={<SimpleWalletTest />} />
            
            {/* Main Pages */}
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/industries" element={<IndustriesPage />} />
            <Route path="/press-release" element={<PressReleasePage />} />
            <Route path="/faqs" element={<FaqsPage />} />
            <Route path="/client-login" element={<ClientLoginPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            
            {/* Industry routes */}
            <Route path="/industries/building-construction" element={<BuildingConstructionPage />} />
            <Route path="/industries/financial-services" element={<FinancialServicesPage />} />
            <Route path="/industries/corporate-advisory" element={<CorporateAdvisoryPage />} />
            <Route path="/industries/international-markets" element={<InternationalMarketsPage />} />
            
            {/* Fund routes */}
            <Route path="/fund/blockchain" element={<BlockchainFundPage />} />
            <Route path="/fund/energies" element={<EnergiesFundPage />} />
            <Route path="/fund/shares-trading" element={<SharesTradingPage />} />
            <Route path="/fund/agricultural" element={<AgriculturalFundPage />} />
            <Route path="/fund/nfp" element={<NFPPage />} />
            <Route path="/fund/renewable-energy" element={<RenewableEnergyPage />} />
            
            {/* Service routes */}
            <Route path="/services/investment-management" element={<InvestmentManagementPage />} />
            <Route path="/services/financial-service" element={<FinancialServicePage />} />
            <Route path="/services/portfolio-management" element={<PortfolioManagementServicePage />} />
            <Route path="/services/financial-advising" element={<FinancialAdvisingPage />} />
            <Route path="/services/wealth-management" element={<WealthManagementPage />} />
            <Route path="/services/financial-planning" element={<FinancialPlanningPage />} />
            <Route path="/services/retirement-planning" element={<RetirementPlanningPage />} />
            <Route path="/services/managed-portfolios" element={<ManagedPortfoliosPage />} />
            <Route path="/services/wealth-estate-planning" element={<WealthEstatePlanningPage />} />
            <Route path="/services/mutual-funds" element={<MutualFundsPage />} />
            <Route path="/services/personal-retirement-account" element={<PersonalRetirementAccountPage />} />
            <Route path="/services/securities-trading-brokerage" element={<SecuritiesTradingBrokeragePage />} />
            <Route path="/services/trustee-registrar-services" element={<TrusteeRegistrarServicesPage />} />
            
            {/* Portfolio routes */}
            <Route path="/portfolio/liquid-token" element={<LiquidTokenPage />} />
            <Route path="/portfolio/bitcoin-trading" element={<BitcoinTradingPage />} />
            <Route path="/portfolio/early-stage-token" element={<EarlyStageTokenPage />} />
            <Route path="/portfolio/staking-fund" element={<StakingFundPage />} />
            
            {/* Buy Crypto Page */}
            <Route path="/buy-crypto" element={<BuyCryptoPage />} />
            
            {/* Earn Crypto Page */}
            <Route path="/earn-crypto" element={<EarnCryptoPage />} />
            
            {/* Exchange Page */}
            <Route path="/exchange" element={<ExchangePage />} />
            
            {/* Futures Trading Page */}
            <Route path="/futures-trading" element={<FuturesTradingPage />} />
            
            {/* Tax Reporting Page */}
            <Route path="/tax-reporting" element={<TaxReportingPage />} />
            
            {/* Apply Now - Full page */}
            <Route path="/apply-now" element={<ApplyNowPage />} />
            
            {/* 404 Page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer />
          <LiveChatWidget />
        </Router>
    </div>
    </ThemeProvider>
  );
}

export default App;